import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/Dialog';
import { useAttendanceStore, type AdjustmentType, type AttendanceSession } from '../../../store/attendanceStore';
import { ADJUSTMENT_OPTIONS, fmtTime, localDateString, toIso, SessionStatusBadge } from './attendanceUi';
import apiClient from '../../../lib/api';

const fieldClass =
    'h-9 w-full rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-50';

const NEEDS_SESSION: AdjustmentType[] = ['MISSED_CHECK_OUT', 'TIME_CORRECTION', 'LOCATION_CORRECTION'];
const hhmm = (iso: string | null) => (iso ? new Date(iso).toTimeString().slice(0, 5) : '');

export interface AdjustmentPreset { date?: string; sessionId?: string; type?: AdjustmentType }

/**
 * Request an attendance correction (employee) or record one directly (HR,
 * mode="admin"). Only the fields the chosen type needs are shown; the server
 * re-validates everything and keeps the original record.
 */
export function AdjustmentDialog({ open, onOpenChange, mode = 'employee', employee, preset, onDone }: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode?: 'employee' | 'admin';
    employee?: { id: string; name: string | null };
    preset?: AdjustmentPreset;
    onDone?: () => void;
}) {
    const { pickableLocations, fetchPickableLocations, fetchDayDetail, requestAdjustment, adminCorrection } = useAttendanceStore();
    const today = localDateString();
    const [type, setType] = useState<AdjustmentType>(preset?.type || 'MISSED_CHECK_OUT');
    const [date, setDate] = useState(preset?.date || today);
    const [sessionId, setSessionId] = useState(preset?.sessionId || '');
    const [sessions, setSessions] = useState<AttendanceSession[]>([]);
    const [start, setStart] = useState('');
    const [finish, setFinish] = useState('');
    const [locationId, setLocationId] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [managers, setManagers] = useState<{ id: string; name: string }[]>([]);
    const [managerId, setManagerId] = useState('');
    const [fallbackName, setFallbackName] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setType(preset?.type || 'MISSED_CHECK_OUT');
        setDate(preset?.date || today);
        setSessionId(preset?.sessionId || '');
        setStart(''); setFinish(''); setLocationId(''); setReason('');
        if (!pickableLocations.length) fetchPickableLocations();

        // Employee mode: load the employee's reporting manager(s) so they can
        // choose who to send the request to.
        if (mode !== 'admin') {
            apiClient.get('/people/reporting-managers/my-manager')
                .then((res) => {
                    const d = res.data?.data ?? res.data ?? {};
                    const list = (Array.isArray(d.managers) ? d.managers : [])
                        .map((m: any) => ({ id: m.id, name: `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() }))
                        .filter((m: any) => m.id && m.name);
                    setManagers(list);
                    setManagerId(list[0]?.id || '');
                    setFallbackName(d.fallback ? `${d.fallback.first_name ?? ''} ${d.fallback.last_name ?? ''}`.trim() : null);
                })
                .catch(() => { setManagers([]); setManagerId(''); setFallbackName(null); });
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sessions of the chosen day (to pick which one to correct).
    useEffect(() => {
        if (!open || !date) return;
        let alive = true;
        fetchDayDetail(date, mode === 'admin' ? employee?.id : undefined)
            .then((d) => alive && setSessions(d.sessions.filter((s) => ['COMPLETED', 'MISSING_CHECKOUT', 'ADJUSTED'].includes(s.status))))
            .catch(() => alive && setSessions([]));
        return () => { alive = false; };
    }, [open, date, mode, employee?.id, fetchDayDetail]);

    const session = sessions.find((s) => s.id === sessionId) || null;
    useEffect(() => {
        // Prefill a time correction with what was recorded, so only the wrong part needs changing.
        if (type === 'TIME_CORRECTION' && session) { setStart(hhmm(session.checkIn)); setFinish(hhmm(session.checkOut)); }
    }, [type, session]);

    const needsSession = NEEDS_SESSION.includes(type);
    const showStart = type === 'MISSED_BOTH' || type === 'MISSED_CHECK_IN' || type === 'TIME_CORRECTION';
    const showFinish = showStart || type === 'MISSED_CHECK_OUT';
    const overnight = showStart && showFinish && start && finish && finish <= start;
    const help = ADJUSTMENT_OPTIONS.find((o) => o.type === type)?.help;
    const activeOffices = pickableLocations;

    const submit = async () => {
        if (needsSession && !sessionId) { toast.error('Choose the session to correct.'); return; }
        if ((type === 'MISSED_BOTH' || type === 'MISSED_CHECK_IN') && (!start || !finish)) { toast.error('Enter both times.'); return; }
        if (type === 'MISSED_CHECK_OUT' && !finish) { toast.error('Enter the time you left.'); return; }
        if (type === 'LOCATION_CORRECTION' && !locationId) { toast.error('Choose the correct location.'); return; }
        if (reason.trim().length < 5) { toast.error('Please explain what happened.'); return; }

        // Finish on the next day when it is earlier than the start (night shift); for a
        // missed check-out, compare with the recorded check-in.
        const startIso = showStart && start ? toIso(date, start) : null;
        let finishIso = showFinish && finish ? toIso(date, finish) : null;
        const reference = startIso || (session ? session.checkIn : null);
        if (finishIso && reference && new Date(finishIso) <= new Date(reference)) {
            const next = new Date(`${date}T00:00:00`); next.setDate(next.getDate() + 1);
            finishIso = toIso(localDateString(next), finish);
        }
        const recordedIn = session ? hhmm(session.checkIn) : '';
        const recordedOut = session ? hhmm(session.checkOut) : '';
        const input = {
            adjustmentType: type,
            requestDate: date,
            sessionId: needsSession ? sessionId : null,
            // For a time correction, send only what changed.
            requestedCheckIn: type === 'TIME_CORRECTION' && start === recordedIn ? null : startIso,
            requestedCheckOut: type === 'TIME_CORRECTION' && finish === recordedOut ? null : finishIso,
            locationId: locationId || null,
            reason: reason.trim(),
            reportingManagerId: mode === 'admin' ? null : (managerId || null),
        };
        if (type === 'TIME_CORRECTION' && !input.requestedCheckIn && !input.requestedCheckOut) {
            toast.error('Change at least one of the times.'); return;
        }
        setBusy(true);
        try {
            if (mode === 'admin' && employee) {
                await adminCorrection({ ...input, employeeId: employee.id });
                toast.success('Correction recorded');
            } else {
                await requestAdjustment(input);
                toast.success('Request sent for review');
            }
            onOpenChange(false);
            onDone?.();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{mode === 'admin' ? `Correct attendance${employee?.name ? ' — ' + employee.name : ''}` : 'Request an adjustment'}</DialogTitle>
                    <DialogDescription>
                        {mode === 'admin'
                            ? 'Recorded immediately as an HR correction. The original record is kept and the change is audited.'
                            : 'Your manager or HR will review it. The original record is kept; nothing changes until it is approved.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid gap-4">
                    <Field label="What needs correcting?" hint={help}>
                        <select className={fieldClass} value={type} onChange={(e) => { setType(e.target.value as AdjustmentType); setStart(''); setFinish(''); }}>
                            {ADJUSTMENT_OPTIONS.map((o) => <option key={o.type} value={o.type}>{o.label}</option>)}
                        </select>
                    </Field>

                    <Field label="Date">
                        <input className={fieldClass} type="date" value={date} max={today} onChange={(e) => { setDate(e.target.value); setSessionId(''); }} />
                    </Field>

                    {needsSession && (
                        <Field label="Session" hint={sessions.length ? undefined : 'No sessions were recorded on this date.'}>
                            <select className={fieldClass} value={sessionId} onChange={(e) => setSessionId(e.target.value)} disabled={!sessions.length}>
                                <option value="">Choose a session</option>
                                {sessions.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        Session {s.number}: {fmtTime(s.checkIn)} → {s.checkOut ? fmtTime(s.checkOut) : 'no check-out'}{s.checkInLocation ? ` · ${s.checkInLocation}` : ''}
                                    </option>
                                ))}
                            </select>
                            {session && <div className="mt-1.5"><SessionStatusBadge status={session.status} /></div>}
                        </Field>
                    )}

                    {(showStart || showFinish) && (
                        <div className="grid grid-cols-2 gap-3">
                            {showStart ? (
                                <Field label={type === 'TIME_CORRECTION' ? 'Correct check-in' : 'Started at'}>
                                    <input className={fieldClass} type="time" value={start} onChange={(e) => setStart(e.target.value)} />
                                </Field>
                            ) : <div />}
                            <Field label={type === 'TIME_CORRECTION' ? 'Correct check-out' : type === 'MISSED_CHECK_OUT' ? 'Left at' : 'Finished at'}
                                hint={overnight ? 'Ends the next day (night shift).' : undefined}>
                                <input className={fieldClass} type="time" value={finish} onChange={(e) => setFinish(e.target.value)} />
                            </Field>
                        </div>
                    )}

                    {(type === 'LOCATION_CORRECTION' || type === 'MISSED_BOTH' || type === 'MISSED_CHECK_IN') && (
                        <Field label={type === 'LOCATION_CORRECTION' ? 'Where you actually worked' : 'Location (optional)'}>
                            <select className={fieldClass} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                                <option value="">{type === 'LOCATION_CORRECTION' ? 'Choose a location' : 'Your primary location'}</option>
                                {activeOffices.map((o) => <option key={o.id} value={o.id}>{o.name}{o.city ? ` · ${o.city}` : ''}</option>)}
                            </select>
                        </Field>
                    )}

                    {mode !== 'admin' && (
                        <Field label="Applying to"
                            hint={managers.length > 1 ? 'You report to more than one manager — choose who should review this.' : undefined}>
                            {managers.length > 0 ? (
                                <select className={fieldClass} value={managerId} onChange={(e) => setManagerId(e.target.value)}>
                                    {managers.map((m, i) => (
                                        <option key={m.id} value={m.id}>{m.name}{i === 0 ? ' (primary)' : ''}</option>
                                    ))}
                                </select>
                            ) : (
                                <div className="rounded-lg border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
                                    {fallbackName ? `${fallbackName} · HR (no reporting manager assigned)` : 'HR will review — ask your admin to assign a reporting manager.'}
                                </div>
                            )}
                        </Field>
                    )}

                    <Field label="Reason" required>
                        <textarea className="min-h-20 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30"
                            value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)}
                            placeholder={mode === 'admin' ? 'e.g. Biometric device was down' : 'e.g. I left at 6:30 PM and forgot to check out'} />
                    </Field>
                </div>

                <DialogFooter>
                    <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} isLoading={busy}>{mode === 'admin' ? 'Record correction' : 'Send request'}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
